// apps/app/src/routes/Setups/Create/state.ts
import type { CreateSetup, Setup } from '@hvf/contracts'
import { createMutation } from '@tanstack/solid-query'
import { createSignal } from 'solid-js'
import { useService } from 'solid-services'
import * as v from 'valibot'
import { useList } from '../List/state'
import { handleCreateSetup } from './handleCreateSetup'

export enum FormSteps {
  details = 'details',
  confirm = 'confirm',
  success = 'success',
}

export function useCreate() {
  const [setupBeingEdited, setSetupBeingEdited] = createSignal<CreateSetup | undefined>()
  const [isCreateOpen, setIsCreateOpen] = createSignal(false)
  const [createSuccess, setCreateSuccess] = createSignal(false)
  const [createError, setCreateError] = createSignal('')
  const [step, setStep] = createSignal<FormSteps>(FormSteps.details)
  const [newSetup, setNewSetup] = createSignal<CreateSetup | undefined>()
  // Tracks the "View Chart" side panel's DOM node so the drawer can exclude it
  // from its outside-click/pointer-blocking logic (it renders outside Drawer.Content via Portal).
  const [chartPanelEl, setChartPanelEl] = createSignal<HTMLElement | null>(null)
  const { refetchCurrent } = useService(useList)()

  // Replaced createResource with createMutation
  const setupMutation = createMutation(() => ({
    mutationFn: async (vars: { setup: CreateSetup; oldSetup?: CreateSetup }) => {
      return handleCreateSetup(vars.setup, vars.oldSetup)
    },
    onSuccess: () => {
      setStep(FormSteps.success)
      setNewSetup(undefined)
      setSetupBeingEdited(undefined)
      setCreateSuccess(true)
      setCreateError('')
      refetchCurrent() // Updates your list view
    },
    onError: (error: Error) => {
      setStep(FormSteps.success)
      setCreateError(error.message || 'An error occurred')
      setCreateSuccess(false)
    },
  }))

  const completeDetailStep = (setup: CreateSetup) => {
    setNewSetup(setup)
    setStep(FormSteps.confirm)
  }

  // Action for explicitly saving as published
  const publishSetup = () => {
    const current = newSetup()
    if (current) {
      setupMutation.mutate({
        setup: { ...current, status: 'published' } as CreateSetup,
        oldSetup: setupBeingEdited(),
      })
    }
  }

  // Action for directly saving as a draft from the first tab
  const saveDraft = (setupValues: CreateSetup) => {
    setupMutation.mutate({
      setup: { ...setupValues, status: 'draft' } as CreateSetup,
      oldSetup: setupBeingEdited(),
    })
  }

  const editSetup = (setup: Setup) => {
    setNewSetup(setup)
    setSetupBeingEdited(setup)
    setIsCreateOpen(true)
  }

  const openCreateSetup = () => {
    setIsCreateOpen(true)
    setSetupBeingEdited(undefined)
    setStep(FormSteps.details)
  }

  const isEditing = () => {
    const uuidSchema = v.pipe(v.string(), v.uuid())
    return v.safeParse(uuidSchema, newSetup()?.id).success
  }

  return {
    setupBeingEdited,
    chartPanelEl,
    completeDetailStep,
    createError,
    createSuccess,
    editSetup,
    isCreateOpen,
    isEditing,
    newSetup,
    openCreateSetup,
    setChartPanelEl,
    setIsCreateOpen,
    setNewSetup,
    setStep,
    step,
    setupMutation, // Exporting the mutation here
    publishSetup,
    saveDraft,
  }
}
